/**
 * Pure grouping helper for the row-action overflow surfaces.
 *
 * Kept in its OWN module (no `execute-action` / entity-registry imports) so the
 * overflow dropdowns, the mobile dropdown, and the mobile sheet all share one
 * grouping implementation that matches the right-click menu — and so it can be
 * unit-tested without loading the heavy action-execution graph.
 */
import { CATEGORY_LABEL, type ActionCategory } from "@/lib/actions/action-categories";
import type { BaseEntity } from "@/components/shared/UniversalDetailPage/types/detailPage";
import type { RowActionItem } from "./use-row-action-items";

export interface RowActionItemGroup<T extends BaseEntity> {
  category: ActionCategory;
  label: string;
  items: RowActionItem<T>[];
}

/**
 * Bucket an ALREADY category-sorted `RowActionItem[]` into labeled groups —
 * the shared shape every overflow surface renders so their grouping matches the
 * right-click menu. Items must be pre-sorted (they are, coming out of
 * `useRowActionItems`), so a simple consecutive-run grouping preserves priority
 * order.
 */
export function groupRowActionItems<T extends BaseEntity>(
  items: RowActionItem<T>[],
): RowActionItemGroup<T>[] {
  const groups: RowActionItemGroup<T>[] = [];
  for (const item of items) {
    const last = groups[groups.length - 1];
    if (last && last.category === item.category) {
      last.items.push(item);
    } else {
      groups.push({ category: item.category, label: CATEGORY_LABEL[item.category], items: [item] });
    }
  }
  return groups;
}
